export function format(first: string, middle: string, last: string): string {
  return (first || '') + (middle ? ` ${middle}` : '') + (last ? ` ${last}` : '');
}

export function toKebabCase(str: string) {
  return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

export function formatDate(date: string): string {
  const parts = date.split('-');
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
}

export function stripHtmlContent(content: string): string {
  const div = document.createElement('div');
  div.innerHTML = content;
  return div.textContent || div.innerText || '';
}

interface ComponentProp {
  name: string;
  type: string;
  docs: string;
  default: string;
}

interface ComponentEvent {
  event: string;
  docs: string;
  detail: string;
}

interface ComponentInfo {
  name: string;
  props: ComponentProp[];
  events: ComponentEvent[];
}

const description = (prop: ComponentProp | ComponentEvent) => (prop.docs || '').trim();

const isText = (prop: ComponentProp) => prop.type === 'string';
const isBoolean = (prop: ComponentProp) => prop.type === 'boolean';
const isEnum = (prop: ComponentProp) => prop.type.includes('|');
const isNumber = (prop: ComponentProp) => prop.type === 'number';

const enumOptions = (prop: ComponentProp) =>
  prop.type
    .split('|')
    .map(t => t.trim())
    .map(t => (t === '""' ? 'default' : t))
    .map(t => t.replace(/^"(.*)"$/, '$1'));

const generateProp = (prop: ComponentProp) => {
  if (isText(prop)) {
    return {
      [prop.name]: {
        control: 'text',
        description: description(prop),
        defaultValue: prop.default,
        table: {
          category: 'properties',
          defaultValue: { summary: prop.default },
          type: { summary: 'string' },
        },
      },
    };
  }

  if (isBoolean(prop)) {
    return {
      [prop.name]: {
        control: 'boolean',
        description: description(prop),
        defaultValue: prop.default,
        table: {
          category: 'properties',
          defaultValue: { summary: prop.default },
          type: { summary: 'boolean' },
        },
      },
    };
  }

  if (isNumber(prop)) {
    return {
      [prop.name]: {
        control: 'number',
        description: description(prop),
        defaultValue: prop.default,
        table: {
          category: 'properties',
          defaultValue: { summary: prop.default },
          type: { summary: 'number' },
        },
      },
    };
  }

  if (isEnum(prop)) {
    return {
      [prop.name]: {
        control: 'select',
        defaultValue: prop.default,
        options: enumOptions(prop),
        description: description(prop),
        table: {
          category: 'properties',
          defaultValue: { summary: prop.default },
          type: { summary: 'enum' },
        },
      },
    };
  }

  return {};
};

const generateProps = (props: ComponentProp[]) => {
  let args = {};
  props.forEach(prop => (args = { ...args, ...generateProp(prop) }));
  return args;
};

const generateEvent = (event: ComponentEvent) => {
  function capitalizeFirstLetter(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  return {
    [`on${capitalizeFirstLetter(event.event)}`]: {
      action: event.event,
      description: description(event),
      table: {
        defaultValue: { summary: event.event },
        category: 'events',
        type: { summary: event.detail },
      },
    },
  };
};

const generateEvents = (events: ComponentEvent[]) => {
  let args = {};
  events.forEach(event => (args = { ...args, ...generateEvent(event) }));
  return args;
};

const extractComponentInfoByTag = (componentsData: any, tag: string): ComponentInfo | null => {
  const component = componentsData.components.find(c => c.tag === tag);

  if (!component) {
    console.error(`Component with tag ${tag} not found`);
    return null;
  }

  const { props, events } = component;

  const propInfo = props.map(prop => ({
    name: prop.name,
    type: prop.type,
    docs: prop.docs,
    default: prop.default || '',
  }));

  const eventInfo = events.map(event => ({
    event: event.event,
    docs: event.docs,
    detail: event.detail,
  }));

  return {
    name: tag,
    props: propInfo,
    events: eventInfo,
  };
};

const generateHTMLTable = (componentInfo: ComponentInfo): string => {
  if (!componentInfo) {
    return `<p>Component not found</p>`;
  }

  const { props, events } = componentInfo;

  const propsRows = props
    .map(
      prop => `
    <tr>
      <td>${prop.name}</td>
      <td>${prop.type}</td>
      <td>${prop.docs}</td>
      <td>${prop.default}</td>
    </tr>
  `,
    )
    .join('');

  const eventsRows = events
    .map(
      event => `
    <tr>
      <td>${event.event}</td>
      <td>${event.detail}</td>
      <td>${event.docs}</td>
    </tr>
  `,
    )
    .join('');

  return `
    <h3>Propriétés</h3>
    <table class="w-full">
      <thead>
        <tr>
          <th>Propriété</th>
          <th>Type</th>
          <th>Description</th>
          <th>Valeur par défaut</th>
        </tr>
      </thead>
      <tbody>
        ${propsRows}
      </tbody>
    </table>
    <h3>Événements</h3>
    <table class="w-full">
      <thead>
        <tr>
          <th>Événement</th>
          <th>Détail</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        ${eventsRows}
      </tbody>
    </table>
  `;
};

export const generateComponentTable = (componentsData: any, tag: string): string => {
  const componentInfo = extractComponentInfoByTag(componentsData, tag);
  return generateHTMLTable(componentInfo);
};

export const withComponentControls = (componentsData: any, { tag }: { tag: string }): any => {
  const component = componentsData.components.find(c => c.tag === tag);
  if (component) {
    const props = component.props;
    const events = component.events;

    return {
      ...generateProps(props as ComponentProp[]),
      ...generateEvents(events as ComponentEvent[]),
    };
  }

  return {};
};

export function extractArgTypes(componentsData: any, subComponentTags: string[]) {
  return subComponentTags.reduce((acc, tag) => {
    const subComponentArgTypes = withComponentControls(componentsData, { tag });
    return { ...acc, ...subComponentArgTypes };
  }, {});
}

export function formatFileSize(sizeInBytes) {
  const sizeInMB = sizeInBytes / (1024 * 1024);
  let formattedSize = sizeInMB.toFixed(2).replace('.', ',');

  if (sizeInMB > 0 && sizeInMB < 0.01) {
    formattedSize = '0,01';
  } else if (sizeInMB === 0) {
    formattedSize = '0,00';
  }

  return `${formattedSize} Mo`;
}

export function hasSlotContent(el: HTMLElement): boolean {
  const slot = el.querySelector('slot');
  return slot && slot.assignedNodes().length > 0;
}
